import { ReactNode } from 'react';
import { CampaignState } from 'modules/domains/campaign';

import {
  AnonSession16,
  AppUninstall16,
  Complaint16,
  Custom16,
  EmailBounce16,
  EmailClick16,
  EmailOpen16,
  EmailSend16,
  EmailSendSkip16,
  EmbeddedClick16,
  EmbeddedImpression16,
  EmbeddedReceived16,
  EmbeddedSession16,
  HostedUnsubscribe16,
  InAppClick16,
  InAppClose16,
  InAppDelete16,
  InAppDelivered16,
  InAppOpen16,
  InAppRecall16,
  InAppSend16,
  InAppSendSkip16,
  InboxImpression16,
  InboxSession16,
  ListSubscribe16,
  ListUnsubscribe16,
  Purchase16,
  PushBounce16,
  PushOpen16,
  PushSend16,
  PushSendSkip16,
  SmsBounce16,
  SmsClick16,
  SmsReceived16,
  SmsSend16,
  SmsSendSkip16,
  SmsUsageInfo16,
  User16,
  WebPushClick16,
  WebPushSend16,
  WebPushSendSkip16,
  WhatsAppLine16,
} from 'ui/icons/aurora/16px';
import { NavJourneys24 } from 'ui/icons/aurora/24px/headerNav/NavJourneys24';

import { PaginationInput, SortDirection, SortInput, Sorting } from 'modules/graphql';
import { CAMPAIGN, EMAIL_CAMPAIGN, RECURRING_CAMPAIGN } from 'utils/fieldConstants';

import { ComparatorSelectType } from './renderers/condition/comparator/types';
import {
  BooleanComparator,
  DateComparator,
  StringComparator,
  NumericComparator,
  GeoLocationComparator,
  CampaignComparator,
  CampaignLabelsComparator,
  ListComparator,
  ObjectComparator,
  BrandAffinityComparator,
  IsOneOfComparator,
  TemplateComparator,
  MessageTypeComparator,
  RegexComparator,
  DefaultComparator,
} from './types';
import { ClientDataType, Combinator, EditableQuery, FieldType } from './types';
import { ListType } from 'modules/domains/list/types/list';

export const DEFAULT_TYPEAHEAD_QUERY_LIMIT: PaginationInput = { offset: 0, limit: 5 };

export const DEFAULT_TYPEAHEAD_SORTING: SortInput = {
  sortBy: Sorting.UpdatedAt,
  sortDirection: SortDirection.Descending,
};

export const DEFAULT_QUERY: EditableQuery = {
  searchQueries: [],
  combinator: Combinator.AND,
};

export const I18N_NAMESPACE = 'queryBuilder';

export const QUERY_PATH_ORIGIN = 'query';

export const DATA_TYPE_TO_ICON: Record<ClientDataType, ReactNode> = {
  [ClientDataType.AnonSession]: <AnonSession16 />,
  [ClientDataType.Bounce]: <EmailBounce16 />,
  [ClientDataType.Click]: <EmailClick16 />,
  [ClientDataType.Complaint]: <Complaint16 />,
  [ClientDataType.CustomEvent]: <Custom16 />,
  [ClientDataType.EmbeddedClick]: <EmbeddedClick16 />,
  [ClientDataType.EmbeddedImpression]: <EmbeddedImpression16 />,
  [ClientDataType.EmbeddedReceived]: <EmbeddedReceived16 />,
  [ClientDataType.EmbeddedSession]: <EmbeddedSession16 />,
  [ClientDataType.HostedUnsubscribeClick]: <HostedUnsubscribe16 />,
  [ClientDataType.JourneyExit]: <NavJourneys24 height={16} width={16} />,
  [ClientDataType.InAppClick]: <InAppClick16 />,
  [ClientDataType.InAppClose]: <InAppClose16 />,
  [ClientDataType.InAppDelete]: <InAppDelete16 />,
  [ClientDataType.InAppDelivery]: <InAppDelivered16 />,
  [ClientDataType.InAppOpen]: <InAppOpen16 />,
  [ClientDataType.InAppRecall]: <InAppRecall16 />,
  [ClientDataType.InAppSend]: <InAppSend16 />,
  [ClientDataType.InAppSendSkip]: <InAppSendSkip16 />,
  [ClientDataType.InboxMessageImpression]: <InboxImpression16 />,
  [ClientDataType.InboxSession]: <InboxSession16 />,
  [ClientDataType.Open]: <EmailOpen16 />,
  [ClientDataType.Purchase]: <Purchase16 />,
  [ClientDataType.PushBounce]: <PushBounce16 />,
  [ClientDataType.PushOpen]: <PushOpen16 />,
  [ClientDataType.PushSend]: <PushSend16 />,
  [ClientDataType.PushSendSkip]: <PushSendSkip16 />,
  [ClientDataType.PushUninstall]: <AppUninstall16 />,
  [ClientDataType.SMSBounce]: <SmsBounce16 />,
  [ClientDataType.SMSClick]: <SmsClick16 />,
  [ClientDataType.SMSReceived]: <SmsReceived16 />,
  [ClientDataType.SMSSend]: <SmsSend16 />,
  [ClientDataType.SMSSendSkip]: <SmsSendSkip16 />,
  [ClientDataType.SMSUsageInfo]: <SmsUsageInfo16 />,
  [ClientDataType.Send]: <EmailSend16 />,
  [ClientDataType.SendSkip]: <EmailSendSkip16 />,
  [ClientDataType.Subscribe]: <ListSubscribe16 />,
  [ClientDataType.UnSubscribe]: <ListUnsubscribe16 />,
  [ClientDataType.User]: <User16 />,
  [ClientDataType.WebPushClick]: <WebPushClick16 />,
  [ClientDataType.WebPushSend]: <WebPushSend16 />,
  [ClientDataType.WebPushSendSkip]: <WebPushSendSkip16 />,
  [ClientDataType.WhatsAppBounce]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppClick]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppConversationOpened]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppDelivery]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppReceived]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppSeen]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppSend]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppSendSkip]: <WhatsAppLine16 />, // Design is making an icon for this
  [ClientDataType.WhatsAppUsageInfo]: <WhatsAppLine16 />, // Design is making an icon for this
};

export const ITBLDS_PREFIX = 'itblDS.';
export const BRAND_AFFINITY_FIELD = `${ITBLDS_PREFIX}brandAffinityLabel`;
export const ITBLDS_PG_PREFIX = `${ITBLDS_PREFIX}predictiveGoals.`;

export const ES_FIELD_UNINDXED_DATA = '_unindexed_data';

export const CAMPAIGN_FIELDS = [CAMPAIGN, RECURRING_CAMPAIGN, EMAIL_CAMPAIGN];
export const OBJECT_FIELD_TYPES = [FieldType.OBJECT, FieldType.NESTED, FieldType.GEO_LOCATION];

// Campaign states excluding archived
export const DISPLAYED_CAMPAIGN_STATES = Object.values(CampaignState).filter(
  (campaignState: CampaignState) =>
    campaignState !== CampaignState.Archived &&
    campaignState !== CampaignState.Recurring &&
    campaignState !== CampaignState.Draft,
);

// List types excluding dynamic
export const DISPLAYED_LIST_TYPES_FOR_SUBSCRIPTION_USER_LIST = [
  ListType.Internal,
  ListType.Standard,
  ListType.Suppression,
];

// All list types
export const DISPLAYED_LIST_TYPES = [
  ListType.Internal,
  ListType.Standard,
  ListType.Suppression,
  ListType.Dynamic,
];

export const COMPARATOR_SELECT_TYPE_TO_COMPARATORS_MAPPING = {
  [ComparatorSelectType.boolean]: BooleanComparator,
  [ComparatorSelectType.date]: DateComparator,
  [ComparatorSelectType.string]: StringComparator,
  [ComparatorSelectType.number]: NumericComparator,
  [ComparatorSelectType.campaign]: CampaignComparator,
  [ComparatorSelectType.campaignLabel]: CampaignLabelsComparator,
  [ComparatorSelectType.geoLocation]: GeoLocationComparator,
  [ComparatorSelectType.list]: ListComparator,
  [ComparatorSelectType.object]: ObjectComparator,
  [ComparatorSelectType.brandAffinity]: BrandAffinityComparator,
  [ComparatorSelectType.isOneOf]: IsOneOfComparator,
  [ComparatorSelectType.template]: TemplateComparator,
  [ComparatorSelectType.messageType]: MessageTypeComparator,
  [ComparatorSelectType.regex]: RegexComparator,
  [ComparatorSelectType.default]: DefaultComparator,
};
